import { Maybe } from '@codegrade/maybe';
import { array } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client, { unauthorizedClient } from '@/client';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
/** @returns The initial state. */
function makeInitialState() {
    return {
        runs: {},
        singleRuns: {},
        singleRunLoaders: {},
        singleCases: {},
        singleCaseLoaders: {},
        cases: {},
        matches: {},
        providers: { pagination: { tag: 'not-started' } },
    };
}
const moduleBuilder = storeBuilder.module('plagiarism', makeInitialState());
/** The store for the Plagiarism. */
export var PlagiarismStore;
(function (PlagiarismStore) {
    /** Resets the entire state for this module to its initial values. */
    PlagiarismStore.commitClearAll = moduleBuilder.commit(state => {
        Object.assign(state, makeInitialState());
    }, 'commitClearAll');
    const commitProviders = moduleBuilder.commit((state, data) => {
        state.providers = data;
    }, 'commitProviders');
    const commitRuns = moduleBuilder.commit((state, { assignmentId, data, }) => {
        for (const el of data.data ?? []) {
            utils.vueSet(state.singleRuns, el.id, el);
        }
        utils.vueSet(state.runs, assignmentId, data);
    }, 'commitRuns');
    const commitMatches = moduleBuilder.commit((state, { caseId, data, }) => {
        utils.vueSet(state.matches, caseId, data);
    }, 'commitMatches');
    const commitRun = moduleBuilder.commit((state, run) => {
        utils.vueSet(state.singleRuns, run.id, run);
    }, 'commitRun');
    const commitSingleRunLoader = moduleBuilder.commit((state, { runId, loader, }) => {
        utils.vueSetOrDelete(state.singleRunLoaders, runId, loader);
    }, 'commitSingleRunLoader');
    const commitCase = moduleBuilder.commit((state, c) => {
        utils.vueSet(state.singleCases, c.id, c);
    }, 'commitCase');
    const commitSingleCaseLoader = moduleBuilder.commit((state, { caseId, loader, }) => {
        utils.vueSetOrDelete(state.singleCaseLoaders, caseId, loader);
    }, 'commitSingleCaseLoader');
    const commitCases = moduleBuilder.commit((state, { runId, data, }) => {
        utils.vueSet(state.cases, runId, data);
    }, 'commitCases');
    /** Gets the paginated list of plagiarism runs for a given assignment. */
    PlagiarismStore.getRuns = moduleBuilder.read(state => 
    /**
     * The actual getter function.
     *
     * @param assignmentId The ID of the assignment.
     * @returns A paginated state object containing the plagiarism runs.
     */
    (assignmentId) => utils.getPaginatedState(state.runs[assignmentId]), 'getRuns');
    /** Gets the details of a single plagiarism run by its ID. */
    PlagiarismStore.getRun = moduleBuilder.read(state => 
    /**
     * The actual getter function.
     *
     * @param runId The ID of the plagiarism run.
     * @returns A `Maybe` containing the run if found, otherwise empty.
     */
    (runId) => Maybe.fromNullable(state.singleRuns[runId]), 'getRun');
    /**
     * Gets the paginated list of plagiarism matches for a given case.
     */
    PlagiarismStore.getMatches = moduleBuilder.read(state => 
    /**
     * The actual getter function.
     *
     * @param caseId The ID of the case.
     * @returns A paginated state object containing the plagiarism matches.
     */
    (caseId) => utils.getPaginatedState(state.matches[caseId]), 'getMatches');
    /**
     * Gets a single plagiarism case by its ID, with a fallback mechanism.
     *
     * It first attempts to find the case in the dedicated cache for single, fully
     * loaded cases (`state.singleCases`). If not found, it falls back to searching
     * within the paginated list of cases for the parent run (`state.cases`).
     */
    PlagiarismStore.getCase = moduleBuilder.read(state => 
    /**
     * The actual getter function.
     *
     * @param root0 The getter payload.
     * @returns A `Maybe` containing the case if found, otherwise empty.
     */
    ({ caseId, runId, }) => Maybe.fromNullable(state.singleCases[caseId]).altLazy(() => array.findMaybe(c => c.id === caseId, state.cases[runId]?.data ?? [])), 'getCase');
    /** Gets the paginated list of plagiarism cases for a given run. */
    PlagiarismStore.getCases = moduleBuilder.read(state => 
    /**
     * The actual getter function.
     *
     * @param runId The ID of the plagiarism run.
     * @returns A paginated state object containing the plagiarism cases.
     */
    (runId) => utils.getPaginatedState(state.cases[runId]), 'getCases');
    /** Fetches and stores a paginated list of plagiarism runs for an assignment. */
    PlagiarismStore.loadRuns = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param root0 The module context.
     * @param root1 The action payload.
     * @returns A promise that resolves when the operation is complete.
     */
    async ({ state }, { assignmentId, toLoad, }) => {
        await utils.loadPaginatedWithForce({
            commitData: data => commitRuns({ assignmentId, data }),
            getData: () => state.runs[assignmentId],
            load: async (nextToken) => client.p.assignment.getAllPlagiarismRuns({ assignmentId, nextToken }),
            toLoad,
        });
    }, 'loadRuns');
    /**
     * Fetches and stores the full details of a single plagiarism run.
     *
     * The `force` flag can be used to refetch the run even if it already exists.
     */
    PlagiarismStore.loadRun = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param root0 The module context.
     * @param root1 The action payload.
     * @returns A promise that resolves when the operation is complete.
     */
    async ({ state }, { runId, force = false, }) => {
        await utils.storeLoadWithMaybeForce({
            addLoader: loader => commitSingleRunLoader({ runId, loader: Maybe.of(loader) }),
            force,
            load: async () => {
                const { body: run } = await client.p.plagiarism.get({ plagiarismId: runId });
                commitRun(run);
            },
            loader: Maybe.fromNullable(state.singleRunLoaders[runId]),
            removeLoader: () => commitSingleRunLoader({ runId, loader: Maybe.empty() }),
            value: Maybe.fromNullable(state.singleRuns[runId]),
        });
    }, 'loadRun');
    /**
     * Fetches and stores the full details of a single plagiarism case.
     *
     * The `force` flag can be used to refetch the case even if it already exists.
     */
    PlagiarismStore.loadCase = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param root0 The module context.
     * @param root1 The action payload.
     * @returns A promise that resolves when the operation is complete.
     */
    async ({ state }, { caseId, runId, force = false, }) => {
        await utils.storeLoadWithMaybeForce({
            addLoader: loader => commitSingleCaseLoader({ caseId, loader: Maybe.of(loader) }),
            force,
            load: async () => {
                const { body } = await client.p.plagiarism.getCase({
                    plagiarismId: runId,
                    caseId,
                });
                commitCase(body);
            },
            loader: Maybe.fromNullable(state.singleCaseLoaders[runId]),
            removeLoader: () => commitSingleCaseLoader({ caseId, loader: Maybe.empty() }),
            value: PlagiarismStore.getCase()({ caseId, runId }),
        });
    }, 'loadCase');
    /** Fetches and stores a paginated list of plagiarism cases for a specific run. */
    PlagiarismStore.loadCases = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param root0 The module context.
     * @param root1 The action payload.
     * @returns A promise that resolves when the operation is complete.
     */
    async ({ state }, { runId, toLoad, }) => {
        await utils.loadPaginatedWithForce({
            commitData: data => commitCases({ runId, data }),
            getData: () => state.cases[runId],
            load: async (nextToken) => await client.p.plagiarism.getCases({
                plagiarismId: runId,
                nextToken,
            }),
            toLoad,
        });
    }, 'loadCases');
    /** Fetches and stores a paginated list of plagiarism matches for a specific case. */
    PlagiarismStore.loadMatches = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param root0 The module context.
     * @param root1 The action payload.
     * @returns A promise that resolves when the operation is complete.
     */
    async ({ state }, { runId, caseId, toLoad, }) => {
        await utils.loadPaginatedWithForce({
            commitData: data => commitMatches({ caseId, data }),
            getData: () => state.matches[caseId],
            load: async (nextToken) => client.p.plagiarism.getMatches({ nextToken, caseId, plagiarismId: runId }),
            toLoad,
        });
    }, 'loadMatches');
    /** Deletes a plagiarism run from the server and removes it from the local state. */
    PlagiarismStore.deleteRun = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param state The module context.
     * @param root1 The action payload.
     * @returns A promise that resolves with an empty response upon success.
     */
    async (state, { assignmentId, runId, }) => {
        const response = await client.p.plagiarism.delete({
            plagiarismId: runId,
        });
        const existingState = state.state.runs[assignmentId];
        commitRuns({
            assignmentId,
            data: {
                ...existingState,
                data: existingState.data?.filter(run => run.id !== runId),
            },
        });
        return response.withoutBody();
    }, 'deleteRun');
    /** Creates a new plagiarism run on the server and adds it to the local state. */
    PlagiarismStore.createRun = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param state The module context.
     * @param root1 The action payload.
     * @returns A promise that resolves with an empty response upon success.
     */
    async (state, { assignmentId, data, }) => {
        const response = await client.p.assignment.createPlagiarismRun({
            assignmentId,
            requestBody: data,
        });
        const existingState = state.state.runs[assignmentId];
        commitRuns({
            assignmentId,
            data: {
                ...existingState,
                data: Array.from(utils.itertools.unique(item => item.id, [response.body, ...(existingState.data ?? [])])),
            },
        });
        return response.withoutBody();
    }, 'createRun');
    /** Gets the paginated list of available plagiarism detection providers. */
    PlagiarismStore.providers = moduleBuilder.read(
    /**
     * The actual getter function.
     *
     * @param state The module's state.
     * @returns A paginated state object containing the providers.
     */
    state => utils.getPaginatedState(state.providers), 'providers');
    /** Fetches and stores the paginated list of plagiarism detection providers. */
    PlagiarismStore.loadProviders = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param root0 The module context.
     * @param root1 The action payload.
     * @returns A promise that resolves when the operation is complete.
     */
    async ({ state }, { toLoad, }) => {
        await utils.loadPaginatedWithForce({
            commitData: commitProviders,
            getData: () => state.providers,
            load: async (nextToken) => unauthorizedClient.p.plagiarism.getProviders({ nextToken }),
            toLoad,
        });
    }, 'loadProviders');
    utils.signal.AFTER_USER_CHANGED.register(async () => {
        PlagiarismStore.commitClearAll();
    });
})(PlagiarismStore || (PlagiarismStore = {}));
